// API Configuration
// Centralized API URL management for the application

const getApiBaseUrl = (): string => {
    // Check for environment variable first
    if (typeof window !== 'undefined') {
        // Client-side: use Next.js public env variable
        return process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000';
    }
    // Server-side
    return process.env.API_URL || process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000';
};

// Export the base URL for direct usage
export const API_BASE_URL = getApiBaseUrl();

export const API_CONFIG = {
    baseUrl: getApiBaseUrl(),

    // Dashboard endpoints
    dashboard: {
        summary: () => `${getApiBaseUrl()}/dashboard-summary`,
    },

    reports: {
        fullPattern: () => `${getApiBaseUrl()}/reports/full-pattern`,
    },

    // Analysis endpoints
    analyze: {
        palm: () => `${getApiBaseUrl()}/analyze`,
        async: () => `${getApiBaseUrl()}/analyze/async`,
        status: (requestId: string) => `${getApiBaseUrl()}/analyze/status/${requestId}`,
        dream: () => `${getApiBaseUrl()}/analyze/dream`,
        planetaryImpact: () => `${getApiBaseUrl()}/analyze/planetary-impact`,
        planetaryImpactAsync: () => `${getApiBaseUrl()}/analyze/planetary-impact/async`,
        planetaryImpactStatus: (requestId: string) => `${getApiBaseUrl()}/analyze/planetary-impact/status/${requestId}`,
        tarot: () => `${getApiBaseUrl()}/analyze/tarot`,
        astrology: () => `${getApiBaseUrl()}/analyze/astrology`,
        numerology: () => `${getApiBaseUrl()}/analyze/numerology`,
        compatibility: () => `${getApiBaseUrl()}/analyze/compatibility`,
        horoscope: () => `${getApiBaseUrl()}/analyze/horoscope`,
        palmGuidance: () => `${getApiBaseUrl()}/analyze/palm-guidance`,
        careerGuidance: () => `${getApiBaseUrl()}/analyze/career-guidance`,
        healthGuidance: () => `${getApiBaseUrl()}/analyze/health-guidance`,
        hybridInsights: () => `${getApiBaseUrl()}/analyze/hybrid-insights`,
    },

    // Palm endpoints
    palm: {
        usage: () => `${getApiBaseUrl()}/palm/usage`,
        chat: () => `${getApiBaseUrl()}/palm/chat`,
        saveScan: () => `${getApiBaseUrl()}/palm/save-scan`,
        history: (userId: string) => `${getApiBaseUrl()}/palm/history/${userId}`,
        compare: () => `${getApiBaseUrl()}/palm/compare`,
        compatibility: () => `${getApiBaseUrl()}/palm/compatibility`,
    },

    // Kundali endpoints
    kundali: {
        generate: () => `${getApiBaseUrl()}/kundali/generate`,
        latest: () => `${getApiBaseUrl()}/kundali/latest`,
        places: (query: string) => `${getApiBaseUrl()}/kundali/places/search?q=${encodeURIComponent(query)}`,
        byId: (id: string) => `${getApiBaseUrl()}/kundali/${id}`,
        report: (id: string) => `${getApiBaseUrl()}/kundali/${id}/report`,
        transits: (id: string, date?: string) => `${getApiBaseUrl()}/kundali/${id}/transits${date ? `?date=${encodeURIComponent(date)}` : ''}`,
        yearlyTiming: (id: string, startDate?: string) => `${getApiBaseUrl()}/kundali/${id}/yearly-timing${startDate ? `?startDate=${encodeURIComponent(startDate)}` : ''}`,
        chat: (id: string) => `${getApiBaseUrl()}/kundali/${id}/chat`,
        compatibility: () => `${getApiBaseUrl()}/kundali/compatibility`,
    },

    // Feature endpoints
    features: {
        astrology: () => `${getApiBaseUrl()}/astrology`,
        numerology: () => `${getApiBaseUrl()}/numerology`,
        dreams: () => `${getApiBaseUrl()}/dreams`,
        tarot: () => `${getApiBaseUrl()}/tarot`,
        palm: () => `${getApiBaseUrl()}/palm-reading`,
        guidance: () => `${getApiBaseUrl()}/guidance`,
        aura: () => `${getApiBaseUrl()}/aura`,
    },

    // Tarot endpoints
    tarot: {
        history: () => `${getApiBaseUrl()}/tarot/history`,
    },

    // Notification endpoints
    notifications: {
        active: () => `${getApiBaseUrl()}/notifications/active`,
    },

    payments: {
        subscription: () => `${getApiBaseUrl()}/payments/subscription`,
        createOrder: () => `${getApiBaseUrl()}/payments/create-order`,
        verify: () => `${getApiBaseUrl()}/payments/verify`,
        cancel: () => `${getApiBaseUrl()}/payments/cancel`,
        resume: () => `${getApiBaseUrl()}/payments/resume`,
    },

    // User endpoints
    user: {
        profile: () => `${getApiBaseUrl()}/user/profile`,
        history: () => `${getApiBaseUrl()}/user/history`,
        planetaryImpact: (limit?: number) => `${getApiBaseUrl()}/user/planetary-impact${limit ? `?limit=${limit}` : ''}`,
        palmReadings: () => `${getApiBaseUrl()}/user/palm-readings`,
        relationshipMemory: () => `${getApiBaseUrl()}/user/relationship-memory`,
        updateStreak: () => `${getApiBaseUrl()}/user/update-streak`,
        dailyPractice: (date?: string) =>
            `${getApiBaseUrl()}/user/daily-practice${date ? `?date=${encodeURIComponent(date)}` : ''}`,
        analytics: () => `${getApiBaseUrl()}/user/analytics`,
        shareAura: () => `${getApiBaseUrl()}/user/share/aura`,
        subscribe: () => `${getApiBaseUrl()}/user/subscribe`,
    },

    analytics: {
        anonymous: () => `${getApiBaseUrl()}/analytics/anonymous`,
    },

    share: {
        aura: (id: string) => `${getApiBaseUrl()}/share/aura/${encodeURIComponent(id)}`,
    },

    // Chat endpoint
    chat: () => `${getApiBaseUrl()}/chat`,

    // Admin endpoints
    admin: {
        users: () => `${getApiBaseUrl()}/admin/users`,
        stats: () => `${getApiBaseUrl()}/admin/stats`,
        revenue: () => `${getApiBaseUrl()}/payments/admin/revenue`,
        feedback: () => `${getApiBaseUrl()}/admin/feedback`,
        notifications: () => `${getApiBaseUrl()}/admin/notifications`,
        notificationById: (id: string) => `${getApiBaseUrl()}/admin/notifications/${id}`,
        grantPremium: () => `${getApiBaseUrl()}/admin/grant-premium`,
        revokePremium: () => `${getApiBaseUrl()}/admin/revoke-premium`,
        toggleAdmin: () => `${getApiBaseUrl()}/admin/toggle-admin`,
        deleteUser: () => `${getApiBaseUrl()}/admin/delete-user`,
        email: {
            send: () => `${getApiBaseUrl()}/admin/email/send`,
            broadcast: () => `${getApiBaseUrl()}/admin/email/broadcast`,
        },
    },
};

// Helper for making authenticated requests
export const fetchWithAuth = async (url: string, token: string, options: RequestInit = {}) => {
    const response = await fetch(url, {
        cache: 'no-store',
        ...options,
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'Cache-Control': 'no-store',
            ...options.headers,
        },
    });
    return response;
};

export default API_CONFIG;
